/**
 * Presentation Thumbnail Generator
 *
 * Generates visual thumbnails from presentation slides using Canvas API.
 * Uses a plain orange background matching the app's theme.
 * Thumbnails are cached in localStorage for performance.
 */

import { SlideData } from '@/types/presentation';

export interface ThumbnailCache {
  [key: string]: string; // presentationId-slideIndex -> dataURL
}

const CACHE_KEY = 'presentation-thumbnails';
const THUMBNAIL_WIDTH = 400;
const THUMBNAIL_HEIGHT = 300;

/**
 * Get cached thumbnails from localStorage
 */
export function getCachedThumbnails(): ThumbnailCache {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.warn('Failed to read thumbnail cache:', error);
    return {};
  }
}

/**
 * Save thumbnail to localStorage cache
 */
function saveThumbnailToCache(key: string, dataURL: string): void {
  try {
    const cache = getCachedThumbnails();
    cache[key] = dataURL;

    // Limit cache size to prevent localStorage overflow
    const entries = Object.entries(cache);
    if (entries.length > 50) {
      // Remove oldest entries (first 10)
      const trimmedCache = Object.fromEntries(entries.slice(10));
      localStorage.setItem(CACHE_KEY, JSON.stringify(trimmedCache));
    } else {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    }
  } catch (error) {
    console.warn('Failed to save thumbnail to cache:', error);
  }
}

/**
 * Generate cache key for a thumbnail
 */
function getCacheKey(presentationId: string, slideIndex: number): string {
  return `${presentationId}-${slideIndex}`;
}

/**
 * Draw plain orange background for thumbnails
 */
function drawHeroGradientBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  // Use plain orange background matching the app's theme
  ctx.fillStyle = '#f97316';  // orange-500
  ctx.fillRect(0, 0, width, height);
}

/**
 * Draw dot pattern overlay (like the hero section)
 */
function drawDotPatternOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const dotSpacing = 40;
  const dotRadius = 1;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';

  for (let x = dotSpacing / 2; x < width; x += dotSpacing) {
    for (let y = dotSpacing / 2; y < height; y += dotSpacing) {
      ctx.beginPath();
      ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Generate thumbnail from slide data using Canvas API
 * Uses the hero section's gradient style and displays the slide title or presentation title
 */
export async function generateThumbnail(
  presentationId: string,
  slide: SlideData,
  slideIndex: number,
  presentationTitle?: string
): Promise<string> {
  // Check cache first
  const cacheKey = getCacheKey(presentationId, slideIndex);
  const cached = getCachedThumbnails();
  if (cached[cacheKey]) {
    return cached[cacheKey];
  }

  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      canvas.width = THUMBNAIL_WIDTH;
      canvas.height = THUMBNAIL_HEIGHT;

      // Draw hero-style gradient background
      drawHeroGradientBackground(ctx, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

      // Draw dot pattern overlay
      drawDotPatternOverlay(ctx, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

      // Get title from slide - check multiple locations
      let title = '';
      let subtitle = '';

      // First check if slide has a direct title property
      if (slide.title) {
        title = slide.title;
      }
      // Then check in props for component-based slides
      else if (slide.props) {
        if (typeof slide.props === 'object') {
          if ('title' in slide.props) {
            title = String(slide.props.title);
          }
          if ('subtitle' in slide.props) {
            subtitle = String(slide.props.subtitle);
          }
        }
      }

      // If no title found, try to extract from component name
      if (!title && slide.component) {
        // Try to get component name - this is a best-effort extraction
        const componentStr = slide.component.toString();
        let componentName = '';
        // Try to extract name from function string
        const nameMatch = componentStr.match(/^function\s+(\w+)/) || componentStr.match(/^(\w+)\s*=>/);
        if (nameMatch) {
          componentName = nameMatch[1];
        }
        if (componentName.includes('Title')) {
          title = 'Title Slide';
        } else if (componentName.includes('Agenda')) {
          title = 'Agenda';
        } else if (componentName.includes('Content')) {
          title = 'Content';
        }
      }

      // Use presentation title as fallback if slide has no title
      const displayTitle = title || presentationTitle || 'Presentation';
      const displaySubtitle = subtitle || (presentationTitle && !title ? '' : subtitle);

      // Text styling - white text with subtle shadow for readability
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (title || presentationTitle) {
        // Draw title with better typography
        ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#ffffff';

        // Add text shadow for better readability
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;

        // Truncate title if too long
        let truncatedTitle = displayTitle;
        if (truncatedTitle.length > 35) {
          truncatedTitle = truncatedTitle.substring(0, 32) + '...';
        }

        // Word wrap for title
        const words = truncatedTitle.split(' ');
        let line = '';
        let y = displaySubtitle ? 115 : 130;
        const lineHeight = 28;
        const maxWidth = THUMBNAIL_WIDTH - 50;

        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);

          if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), THUMBNAIL_WIDTH / 2, y);
            line = words[i] + ' ';
            y += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), THUMBNAIL_WIDTH / 2, y);

        // Reset shadow for subtitle
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Draw subtitle if exists
        if (displaySubtitle) {
          ctx.font = '14px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';

          const truncatedSubtitle = displaySubtitle.length > 50
            ? displaySubtitle.substring(0, 47) + '...'
            : displaySubtitle;

          ctx.fillText(truncatedSubtitle, THUMBNAIL_WIDTH / 2, y + 30);
        }
      }

      // Draw slide number badge in corner
      const badgeSize = 26;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.beginPath();
      ctx.roundRect(THUMBNAIL_WIDTH - badgeSize - 10, 10, badgeSize, badgeSize, 8);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${slideIndex + 1}`, THUMBNAIL_WIDTH - badgeSize / 2 - 10, 10 + badgeSize / 2);

      // Convert to data URL
      const dataURL = canvas.toDataURL('image/png', 0.9);

      // Cache the thumbnail
      saveThumbnailToCache(cacheKey, dataURL);

      resolve(dataURL);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
      reject(error);
    }
  });
}

/**
 * Generate a professional, modern thumbnail using the hero section's gradient style
 */
export function generateFallbackThumbnail(title: string, slideCount?: number): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  canvas.width = THUMBNAIL_WIDTH;
  canvas.height = THUMBNAIL_HEIGHT;

  // Draw hero-style gradient background
  drawHeroGradientBackground(ctx, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

  // Draw dot pattern overlay
  drawDotPatternOverlay(ctx, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

  // Draw title with better typography
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Add text shadow for better readability
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 2;

  ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#ffffff';

  // Truncate title if needed
  if (title.length > 35) {
    title = title.substring(0, 32) + '...';
  }

  // Word wrap for title
  const words = title.split(' ');
  let line = '';
  let y = slideCount ? 135 : 150;
  const lineHeight = 30;
  const maxWidth = THUMBNAIL_WIDTH - 50;

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), THUMBNAIL_WIDTH / 2, y);
      line = words[i] + ' ';
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), THUMBNAIL_WIDTH / 2, y);

  // Reset shadow for badge
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  // Draw slide count badge at bottom
  if (slideCount) {
    const badgeWidth = 60;
    const badgeHeight = 24;
    const badgeX = (THUMBNAIL_WIDTH - badgeWidth) / 2;
    const badgeY = THUMBNAIL_HEIGHT - badgeHeight - 15;

    // Badge background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 12);
    ctx.fill();

    // Badge text
    ctx.fillStyle = '#ffffff';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${slideCount} slides`, THUMBNAIL_WIDTH / 2, badgeY + badgeHeight / 2);
  }

  return canvas.toDataURL('image/png', 0.9);
}

/**
 * Clear all cached thumbnails
 */
export function clearThumbnailCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear thumbnail cache:', error);
  }
}

/**
 * Pre-generate thumbnails for all slides in a presentation
 */
export async function generatePresentationThumbnails(
  presentationId: string,
  slides: SlideData[],
  presentationTitle?: string
): Promise<string[]> {
  const thumbnails: string[] = [];

  for (let i = 0; i < slides.length; i++) {
    try {
      const thumbnail = await generateThumbnail(presentationId, slides[i], i, presentationTitle);
      thumbnails.push(thumbnail);
    } catch (error) {
      console.error(`Failed to generate thumbnail for slide ${i}:`, error);
      // Use fallback on error
      thumbnails.push(generateFallbackThumbnail(presentationTitle || `Slide ${i + 1}`));
    }
  }

  return thumbnails;
}
